// eslint-disable-next-line no-restricted-imports
import { prop } from 'ramda';
import type { ContentLocaleEnum } from '@engage/api-v2-models';
import { mapRideToClass } from '@engage/classes/mappers';
import type { Member } from '@engage/members/models/Member';
import { RelationshipCategory, MemberListGroup } from '@engage/members/models/Member';
import type { ApiClassDetail, ApiSegment, ApiFriendsResponse } from './apiTypes';
import type { ClassDetail, Segment } from './models';
import { MetricsType } from './models';

const toMetricsType = (rawMetricsType: string): MetricsType => {
  switch (rawMetricsType) {
    case MetricsType.Running:
      return MetricsType.Running;
    case MetricsType.Floor:
      return MetricsType.Floor;
    case MetricsType.Cycling:
    default:
      return MetricsType.Running;
  }
};

export const toSegment = (apiSegment: ApiSegment): Segment => ({
  ...apiSegment,
  metricsType: toMetricsType(apiSegment.metricsType),
});

const isDubbedLanguage = (str?: string): str is ContentLocaleEnum => {
  /** TODO: current api types generation uses `--useUnionTypes` flag, which generates only types, no runtime objects.
   * Example:
   * export type ContentLocaleEnum = 'de-DE' | 'en-US' | 'es-ES' | 'fr-FR' // packages/@engage/api-v2-models/generated/ContentLocaleEnum.ts
   *
   * This type doesnt allow runtime validation using type guards.
   *
   * Temporary solution: only cast incoming data accordingly to the generated files.
   * */
  return true;
};

type ApiLanguageSelection = NonNullable<ApiClassDetail['languageSelections']>[number];

type WithContentLocale<T> = T extends unknown
  ? Omit<T, 'locale'> & { locale: ContentLocaleEnum }
  : never;

export type LanguageSelection = WithContentLocale<ApiLanguageSelection>;

const isLanguageSelection = (
  selection: ApiLanguageSelection,
): selection is LanguageSelection => isDubbedLanguage(selection.locale);

const toDubbedLanguages = (
  apiDubbedLanguages: ApiClassDetail['dubbedLanguages'],
): ContentLocaleEnum[] | undefined => {
  return apiDubbedLanguages?.filter(isDubbedLanguage);
};

const toLanguageSelection = (
  apiLanguageSelection: ApiClassDetail['languageSelections'],
): LanguageSelection[] | undefined => {
  return apiLanguageSelection?.filter(isLanguageSelection);
};

const toDetail = (apiDetail: ApiClassDetail): ClassDetail => ({
  playlist: apiDetail.playlist,
  radioData: apiDetail.radioData,
  segments: apiDetail.segments?.segmentList?.map(toSegment),
  ride: apiDetail.ride,
  averages: apiDetail.averages,
  sampledTopTags: apiDetail.sampledTopTags,
  languageSelections: toLanguageSelection(apiDetail.languageSelections),
  dubbedLanguages: toDubbedLanguages(apiDetail.dubbedLanguages),
});

export const toClassDetail = (apiDetail: ApiClassDetail) => ({
  class: mapRideToClass({ ...apiDetail.ride, ...{ radioData: apiDetail.radioData } }),
  detail: toDetail(apiDetail),
});

export const toFriendsList = (apiDetail: ApiFriendsResponse): Member[] =>
  apiDetail.data.map(apiMember => ({
    ...prop('user', apiMember),
    relationship: { meToUser: RelationshipCategory.Following },
    listGroup: MemberListGroup.Following,
  }));
